/**
 * VISTOERA_COMPETITIVE_CONTENT_SHARE_TEXT_URL_EXTRACTION_P0
 *
 * Platform-neutral extractor for competitive-content source URLs.
 * Accepts a bare URL or a full Douyin / Xiaohongshu "复制链接" share paste.
 * Mirrors backend ``normalize_reference_source_input`` host allowlist —
 * never invents a second set of domains.
 */

export type ReferencePlatformId =
  | 'douyin'
  | 'xiaohongshu'
  | 'weixin_channels'
  | 'bilibili'
  | 'youtube'
  | 'kuaishou'
  | 'unknown'

const PLATFORM_HOST_PATTERNS: Array<{ platform: ReferencePlatformId; re: RegExp }> = [
  { platform: 'douyin', re: /(^|\.)(douyin\.com|iesdouyin\.com)$/i },
  { platform: 'xiaohongshu', re: /(^|\.)(xiaohongshu\.com|xhslink\.com|xhslink\.cn)$/i },
  { platform: 'weixin_channels', re: /(^|\.)channels\.weixin\.qq\.com$/i },
  { platform: 'bilibili', re: /(^|\.)(bilibili\.com|b23\.tv)$/i },
  { platform: 'youtube', re: /(^|\.)(youtube\.com|youtu\.be)$/i },
  { platform: 'kuaishou', re: /(^|\.)(kuaishou\.com|gifshow\.com)$/i },
]

const PLATFORM_LABELS: Record<ReferencePlatformId, string> = {
  douyin: '抖音',
  xiaohongshu: '小红书',
  weixin_channels: '视频号',
  bilibili: 'B站',
  youtube: 'YouTube',
  kuaishou: '快手',
  unknown: '未知平台',
}

const URL_IN_TEXT_RE = /https?:\/\/[^\s<>"'`]+/gi
const TRAILING_PUNCT_RE = /[)\]}>.,;:!?，。！？、；：”’》】）」』]+$/u

function cleanUrl(raw: string): string {
  return raw.trim().replace(TRAILING_PUNCT_RE, '').trim()
}

export function detectReferencePlatform(sourceUrl: string): ReferencePlatformId {
  try {
    const host = new URL(sourceUrl.trim()).hostname.toLowerCase()
    for (const entry of PLATFORM_HOST_PATTERNS) {
      if (entry.re.test(host)) return entry.platform
    }
  } catch {
    return 'unknown'
  }
  return 'unknown'
}

export function platformLabelZh(platform: ReferencePlatformId): string {
  return PLATFORM_LABELS[platform] || PLATFORM_LABELS.unknown
}

/** All supported URLs in appearance order (deduped). */
export function extractReferenceContentUrls(rawInput: string): string[] {
  const text = (rawInput || '').trim()
  if (!text) return []

  if (/^https?:\/\/\S+$/i.test(text)) {
    const cleaned = cleanUrl(text)
    return detectReferencePlatform(cleaned) === 'unknown' ? [] : [cleaned]
  }

  const found: string[] = []
  const seen = new Set<string>()
  const matches = text.match(URL_IN_TEXT_RE) || []
  for (const match of matches) {
    const candidate = cleanUrl(match)
    if (!candidate) continue
    if (detectReferencePlatform(candidate) === 'unknown') continue
    const key = candidate.replace(/\/+$/, '').toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    found.push(candidate)
  }
  return found
}

export type ExtractReferenceContentUrlResult =
  | { ok: true; sourceUrl: string; platform: ReferencePlatformId; platformLabel: string }
  | { ok: false; reason: 'empty' | 'no_supported_url' | 'unsupported_platform' }

/**
 * V1 deterministic rule: first supported platform URL wins.
 * Unsupported / tracking URLs are ignored.
 */
export function extractReferenceContentUrl(rawInput: string): ExtractReferenceContentUrlResult {
  const text = (rawInput || '').trim()
  if (!text) return { ok: false, reason: 'empty' }

  const supported = extractReferenceContentUrls(text)
  if (supported.length > 0) {
    const sourceUrl = supported[0]
    const platform = detectReferencePlatform(sourceUrl)
    return { ok: true, sourceUrl, platform, platformLabel: platformLabelZh(platform) }
  }

  const anyUrl = URL_IN_TEXT_RE.test(text)
  // Reset lastIndex after global regex test
  URL_IN_TEXT_RE.lastIndex = 0
  if (anyUrl) return { ok: false, reason: 'unsupported_platform' }
  return { ok: false, reason: 'no_supported_url' }
}

export const SHARE_TEXT_VALIDATION_MESSAGE =
  '未识别到有效的内容链接，请重新复制内容链接后粘贴。'

export const UNSUPPORTED_PLATFORM_MESSAGE =
  '暂不支持该平台的内容链接，请粘贴已支持平台的链接或分享文本'
